export type ScriptFamily =
  | "latin"
  | "cyrillic"
  | "greek"
  | "cjk"
  | "japanese"
  | "korean"
  | "arabic"
  | "syriac"
  | "armenian"
  | "georgian"
  | "thai"
  | "kannada"
  | "hebrew";

export type TextDirection = "ltr" | "rtl";

export interface LanguageConfig {
  code: string;
  name: string;
  nativeName: string;
  script: ScriptFamily;
  direction: TextDirection;
  hasRomanization: boolean;
  hasVocalization: boolean;
  hasNastaliq: boolean;
  fontVar: string;
  lineHeightVar: string;
}

const CONFIGS: Record<string, LanguageConfig> = {
  KOR: {
    code: "KOR",
    name: "Korean",
    nativeName: "한국어",
    script: "korean",
    direction: "ltr",
    hasRomanization: true,
    hasVocalization: false,
    hasNastaliq: false,
    fontVar: "--font-script-korean",
    lineHeightVar: "--lh-cjk",
  },
  JPN: {
    code: "JPN",
    name: "Japanese",
    nativeName: "日本語",
    script: "japanese",
    direction: "ltr",
    hasRomanization: true,
    hasVocalization: false,
    hasNastaliq: false,
    fontVar: "--font-script-japanese",
    lineHeightVar: "--lh-cjk",
  },
  CMN_CN: {
    code: "CMN_CN",
    name: "Mandarin Chinese (China)",
    nativeName: "普通话",
    script: "cjk",
    direction: "ltr",
    hasRomanization: true,
    hasVocalization: false,
    hasNastaliq: false,
    fontVar: "--font-script-cjk",
    lineHeightVar: "--lh-cjk",
  },
  CMN_TW: {
    code: "CMN_TW",
    name: "Mandarin Chinese (Taiwan)",
    nativeName: "國語",
    script: "cjk",
    direction: "ltr",
    hasRomanization: true,
    hasVocalization: false,
    hasNastaliq: false,
    fontVar: "--font-script-cjk",
    lineHeightVar: "--lh-cjk",
  },
  PBT: {
    code: "PBT",
    name: "Pashto",
    nativeName: "پښتو",
    script: "arabic",
    direction: "rtl",
    hasRomanization: true,
    hasVocalization: false,
    hasNastaliq: true,
    fontVar: "--font-script-arabic",
    lineHeightVar: "--lh-arabic",
  },
  PES: {
    code: "PES",
    name: "Persian",
    nativeName: "فارسی",
    script: "arabic",
    direction: "rtl",
    hasRomanization: true,
    hasVocalization: true,
    hasNastaliq: true,
    fontVar: "--font-script-arabic",
    lineHeightVar: "--lh-arabic",
  },
  RUS: {
    code: "RUS",
    name: "Russian",
    nativeName: "Русский",
    script: "cyrillic",
    direction: "ltr",
    hasRomanization: true,
    hasVocalization: true,
    hasNastaliq: false,
    fontVar: "--font-script-cyrillic",
    lineHeightVar: "--lh-cyrillic",
  },
  KAN: {
    code: "KAN",
    name: "Kannada",
    nativeName: "ಕನ್ನಡ",
    script: "kannada",
    direction: "ltr",
    hasRomanization: true,
    hasVocalization: false,
    hasNastaliq: false,
    fontVar: "--font-script-kannada",
    lineHeightVar: "--lh-indic",
  },
  THA: {
    code: "THA",
    name: "Thai",
    nativeName: "ภาษาไทย",
    script: "thai",
    direction: "ltr",
    hasRomanization: true,
    hasVocalization: false,
    hasNastaliq: false,
    fontVar: "--font-script-thai",
    lineHeightVar: "--lh-thai",
  },
  ARZ: {
    code: "ARZ",
    name: "Egyptian Arabic",
    nativeName: "مصرى",
    script: "arabic",
    direction: "rtl",
    hasRomanization: true,
    hasVocalization: true,
    hasNastaliq: false,
    fontVar: "--font-script-arabic",
    lineHeightVar: "--lh-arabic",
  },
  ARB: {
    code: "ARB",
    name: "Arabic",
    nativeName: "العربية",
    script: "arabic",
    direction: "rtl",
    hasRomanization: true,
    hasVocalization: true,
    hasNastaliq: false,
    fontVar: "--font-script-arabic",
    lineHeightVar: "--lh-arabic",
  },
  APC: {
    code: "APC",
    name: "Levantine Arabic",
    nativeName: "شامي",
    script: "arabic",
    direction: "rtl",
    hasRomanization: true,
    hasVocalization: true,
    hasNastaliq: false,
    fontVar: "--font-script-arabic",
    lineHeightVar: "--lh-arabic",
  },
  AFB: {
    code: "AFB",
    name: "Gulf Arabic",
    nativeName: "خليجي",
    script: "arabic",
    direction: "rtl",
    hasRomanization: true,
    hasVocalization: true,
    hasNastaliq: false,
    fontVar: "--font-script-arabic",
    lineHeightVar: "--lh-arabic",
  },
  ARC: {
    code: "ARC",
    name: "Aramaic",
    nativeName: "ܐܪܡܝܐ",
    script: "syriac",
    direction: "rtl",
    hasRomanization: true,
    hasVocalization: false,
    hasNastaliq: false,
    fontVar: "--font-script-syriac",
    lineHeightVar: "--lh-arabic",
  },
  HYE: {
    code: "HYE",
    name: "Armenian",
    nativeName: "Հայերեն",
    script: "armenian",
    direction: "ltr",
    hasRomanization: true,
    hasVocalization: false,
    hasNastaliq: false,
    fontVar: "--font-script-armenian",
    lineHeightVar: "--lh-latin",
  },
  KAT: {
    code: "KAT",
    name: "Georgian",
    nativeName: "ქართული",
    script: "georgian",
    direction: "ltr",
    hasRomanization: true,
    hasVocalization: false,
    hasNastaliq: false,
    fontVar: "--font-script-georgian",
    lineHeightVar: "--lh-latin",
  },
  ELL: {
    code: "ELL",
    name: "Greek",
    nativeName: "Ελληνικά",
    script: "greek",
    direction: "ltr",
    hasRomanization: true,
    hasVocalization: false,
    hasNastaliq: false,
    fontVar: "--font-script-greek",
    lineHeightVar: "--lh-latin",
  },
  HEB: {
    code: "HEB",
    name: "Hebrew",
    nativeName: "עברית",
    script: "hebrew",
    direction: "rtl",
    hasRomanization: true,
    hasVocalization: true,
    hasNastaliq: false,
    fontVar: "--font-script-hebrew",
    lineHeightVar: "--lh-arabic",
  },
  // Tier 1 Latin-script languages
  FRA: { code: "FRA", name: "French", nativeName: "Français", script: "latin", direction: "ltr", hasRomanization: false, hasVocalization: false, hasNastaliq: false, fontVar: "--font-script-latin", lineHeightVar: "--lh-latin" },
  DEU: { code: "DEU", name: "German", nativeName: "Deutsch", script: "latin", direction: "ltr", hasRomanization: false, hasVocalization: false, hasNastaliq: false, fontVar: "--font-script-latin", lineHeightVar: "--lh-latin" },
  ITA: { code: "ITA", name: "Italian", nativeName: "Italiano", script: "latin", direction: "ltr", hasRomanization: false, hasVocalization: false, hasNastaliq: false, fontVar: "--font-script-latin", lineHeightVar: "--lh-latin" },
  SPA_ES: { code: "SPA_ES", name: "Spanish (Spain)", nativeName: "Español", script: "latin", direction: "ltr", hasRomanization: false, hasVocalization: false, hasNastaliq: false, fontVar: "--font-script-latin", lineHeightVar: "--lh-latin" },
  SPA_MX: { code: "SPA_MX", name: "Spanish (Mexico)", nativeName: "Español (México)", script: "latin", direction: "ltr", hasRomanization: false, hasVocalization: false, hasNastaliq: false, fontVar: "--font-script-latin", lineHeightVar: "--lh-latin" },
  SPA_AR: { code: "SPA_AR", name: "Spanish (Argentina)", nativeName: "Español (Argentina)", script: "latin", direction: "ltr", hasRomanization: false, hasVocalization: false, hasNastaliq: false, fontVar: "--font-script-latin", lineHeightVar: "--lh-latin" },
  POR_BR: { code: "POR_BR", name: "Portuguese (Brazil)", nativeName: "Português (Brasil)", script: "latin", direction: "ltr", hasRomanization: false, hasVocalization: false, hasNastaliq: false, fontVar: "--font-script-latin", lineHeightVar: "--lh-latin" },
  POR_PT: { code: "POR_PT", name: "Portuguese (Portugal)", nativeName: "Português", script: "latin", direction: "ltr", hasRomanization: false, hasVocalization: false, hasNastaliq: false, fontVar: "--font-script-latin", lineHeightVar: "--lh-latin" },
  POL: { code: "POL", name: "Polish", nativeName: "Polski", script: "latin", direction: "ltr", hasRomanization: false, hasVocalization: false, hasNastaliq: false, fontVar: "--font-script-latin", lineHeightVar: "--lh-latin" },
  TUR: { code: "TUR", name: "Turkish", nativeName: "Türkçe", script: "latin", direction: "ltr", hasRomanization: false, hasVocalization: false, hasNastaliq: false, fontVar: "--font-script-latin", lineHeightVar: "--lh-latin" },
  VIE: { code: "VIE", name: "Vietnamese", nativeName: "Tiếng Việt", script: "latin", direction: "ltr", hasRomanization: false, hasVocalization: false, hasNastaliq: false, fontVar: "--font-script-latin", lineHeightVar: "--lh-latin" },
  IND: { code: "IND", name: "Indonesian", nativeName: "Bahasa Indonesia", script: "latin", direction: "ltr", hasRomanization: false, hasVocalization: false, hasNastaliq: false, fontVar: "--font-script-latin", lineHeightVar: "--lh-latin" },
  ENG_US: { code: "ENG_US", name: "English (US)", nativeName: "English", script: "latin", direction: "ltr", hasRomanization: false, hasVocalization: false, hasNastaliq: false, fontVar: "--font-script-latin", lineHeightVar: "--lh-latin" },
  ENG_GB: { code: "ENG_GB", name: "English (UK)", nativeName: "English (UK)", script: "latin", direction: "ltr", hasRomanization: false, hasVocalization: false, hasNastaliq: false, fontVar: "--font-script-latin", lineHeightVar: "--lh-latin" },
};

export function getLanguageConfig(code: string): LanguageConfig {
  return CONFIGS[code] ?? {
    code,
    name: code,
    nativeName: code,
    script: "latin",
    direction: "ltr",
    hasRomanization: false,
    hasVocalization: false,
    hasNastaliq: false,
    fontVar: "--font-script-latin",
    lineHeightVar: "--lh-latin",
  };
}

export function isRtl(code: string): boolean {
  return getLanguageConfig(code).direction === "rtl";
}

export function getScriptFontVar(code: string, nastaliqEnabled: boolean): string {
  const config = getLanguageConfig(code);
  if (nastaliqEnabled && config.hasNastaliq) {
    return "--font-script-nastaliq";
  }
  return config.fontVar;
}

export function getAllLanguageCodes(): string[] {
  return Object.keys(CONFIGS);
}
